import { Component, DestroyRef, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { NavBarComponent } from '../nav-bar/nav-bar.component';
import { ActivatedRoute } from '@angular/router';
import { MappingService } from '../services/mapping.service';
import { FarmManagementService } from '../services/farm-management.service';
import { SyncService } from '../services/sync-service';
import { FieldDashboardDataService } from './field-dashboard-data.service';
import { DashboardOrigin, FieldDashboardNavService } from './field-dashboard-nav.service';
import { FieldIntegrationStatus } from '../models/integration-status';
import { chipClasses, dotClasses, statusDot } from './integration-status/integration-status.util';
import { isoDate, seasonFromLabel, seasonLabel } from './macronutrients/season-period';
import { DropdownOption } from '../ui-kit/dropdown/dropdown.component';

/** The integration status "?" button's id: rendered in the Field Details card, focused from here. */
export const INTEGRATION_STATUS_BUTTON_ID = 'integration-status-button';

@Component({
  selector: 'app-field-dashboard-view',
  templateUrl: './field-dashboard-view.component.html',
  styleUrls: ['./field-dashboard-view.component.css'],
  standalone: false,
  encapsulation: ViewEncapsulation.None
})
export class FieldDashboardViewComponent implements OnInit {
  startDate: Date | null = null;
  endDate: Date | null = null;
  selectedSeason: string = '';
  seasons: string[] = [];
  /** The season dropdown's options, built once with the seasons. */
  seasonOptions: DropdownOption[] = [];

  /** The integration drawer and its "?" button exist on Southern Gateway only. */
  readonly sg = inject(FieldDashboardNavService).isSouthernGateway();
  readonly integrationStatus = signal<FieldIntegrationStatus | null>(null);
  readonly integrationStatusLoading = signal(false);
  readonly integrationStatusFailed = signal(false);
  readonly drawerOpen = signal(false);
  /** The "?" button's dot and its spoken label. Null while the first answer loads. */
  readonly statusDot = computed(() => statusDot(this.integrationStatus()?.overall ?? null, this.integrationStatusFailed()));
  readonly dotClasses = dotClasses;
  readonly chipClasses = chipClasses;

  private readonly destroyRef = inject(DestroyRef);
  /** Field ids to load the status for. One call at a time: a newer request cancels the older, so its answer never lands. */
  private readonly statusRequests = new Subject<string>();

  constructor(
    private nav: NavBarComponent,
    private route: ActivatedRoute,
    private mappingService: MappingService,
    private farmService: FarmManagementService,
    private syncService: SyncService,
    private dataService: FieldDashboardDataService
  ) { }

  ngOnInit(): void {
    this.nav.toggleLoading(false);
    this.initializeSeasons();
    this.initializeDateRange();
    this.watchIntegrationStatus();
    
    this.route.paramMap.subscribe(params => {
      const fieldId = params.get('id');
      this.dataService.updateFieldData({ fieldId });
      this.loadFieldData(fieldId);
      this.loadIntegrationStatus(fieldId);
    });
    
    this.route.queryParamMap.subscribe(queryParams => {
      const farmId = queryParams.get('farmId');
      const tenantId = queryParams.get('tenantId');
      // Set on every visit: the data service outlives the page, and a visit from mapping must not
      // inherit "maps" from the one before.
      const from: DashboardOrigin = queryParams.get('from') === 'maps' ? 'maps' : 'mapping';
      this.dataService.updateFieldData({ farmId, tenantId, from });
      if (farmId && tenantId) {
        this.loadFarmData(farmId, tenantId);
      }
    });

    this.dataService.fieldContextReload$.subscribe(() => {
      const now = new Date();
      const cropYear = now.getMonth() >= 5 ? now.getFullYear() + 1 : now.getFullYear();
      this.loadFieldContext(this.dataService.getFieldData().fieldId, cropYear);
    });
  }

  loadFieldData(fieldId: string): void {
    if (fieldId) {
      this.dataService.updateFieldData({ fieldInfoLoading: true });
      this.mappingService.getFieldById(Number(fieldId)).subscribe({
        next: (field: any) => {
          this.dataService.updateFieldData({ fieldName: field.name });
        },
        error: (error) => {
          console.error('Error loading field data:', error);
        }
      });

      const now = new Date();
      const cropYear = now.getMonth() >= 5 ? now.getFullYear() + 1 : now.getFullYear();
      this.loadFieldContext(fieldId, cropYear);
      this.syncService.getFieldInfo(fieldId, cropYear).subscribe({
        next: (info) => {
          this.dataService.updateFieldData({ crop: info.crop, cultivar: info.cultivar, fieldInfoLoading: false });
        },
        error: (error) => {
          console.error('Error loading field info:', error);
        }
      });
    }
  }

  /**
   * The link status does not depend on the crop year but the rest-break date does, so this is
   * refreshed wherever the crop year changes rather than fetched once.
   */
  loadFieldContext(fieldId: string, cropYear: number): void {
    if (!fieldId) {
      return;
    }
    this.dataService.updateFieldData({ fieldContextLoading: true, fieldContextError: false });
    this.syncService.getFieldContext(fieldId, cropYear).subscribe({
      next: (fieldContext) => {
        this.dataService.updateFieldData({ fieldContext, fieldContextLoading: false, fieldContextError: false });
      },
      error: (error) => {
        console.error('Error loading field context:', error);
        // Distinguished from "not linked": a failed call must not be shown as a known-empty link.
        this.dataService.updateFieldData({ fieldContext: null, fieldContextLoading: false, fieldContextError: true });
      }
    });
  }

  /**
   * Southern Gateway only. The status does not depend on the season or dates, so it loads with the
   * field and again after a link change, not on a date change.
   */
  private watchIntegrationStatus(): void {
    if (!this.sg) {
      return;
    }
    this.statusRequests.pipe(
      switchMap(fieldId => this.syncService.getFieldIntegrationStatus(fieldId).pipe(
        map(status => ({ status })),
        catchError(error => {
          console.error('Error loading integration status:', error);
          return of({ status: null as FieldIntegrationStatus | null });
        })
      )),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ status }) => {
      this.integrationStatus.set(status);
      this.integrationStatusFailed.set(status === null);
      this.integrationStatusLoading.set(false);
    });
    // Its own subscription, ended with the page: one link save must cost one status call, however
    // many times the page was visited.
    this.dataService.fieldContextReload$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadIntegrationStatus(this.dataService.getFieldData().fieldId));
  }

  loadIntegrationStatus(fieldId: string): void {
    if (!this.sg || !fieldId) {
      return;
    }
    const shown = this.integrationStatus();
    if (shown && String(shown.fieldId) !== String(fieldId)) {
      // Never show the last field's status against this one while it loads.
      this.integrationStatus.set(null);
    }
    this.integrationStatusLoading.set(true);
    this.integrationStatusFailed.set(false);
    this.statusRequests.next(fieldId);
  }

  retryIntegrationStatus(): void {
    this.loadIntegrationStatus(this.dataService.getFieldData().fieldId);
  }

  toggleDrawer(): void {
    this.drawerOpen.set(!this.drawerOpen());
  }

  /**
   * Closed from inside the drawer: focus goes back to the button that opened it. The button lives in
   * the Field Details card, a child route, so it is found by its id rather than by a view query.
   */
  closeDrawer(): void {
    this.drawerOpen.set(false);
    setTimeout(() => document.getElementById(INTEGRATION_STATUS_BUTTON_ID)?.focus());
  }

  loadFarmData(farmId: string, tenantId: string): void {
    this.farmService.getTenantFarms(Number(tenantId)).subscribe({
      next: (farms: any[]) => {
        const farm = farms.find(f => f.id === Number(farmId));
        if (farm) {
          this.dataService.updateFieldData({
            farmName: farm.name || '',
            numberOfFields: farm.numberOfFields || 0,
            totalHectares: farm.size || 0,
            country: farm.country || ''
          });
        }
      },
      error: (error) => {
        console.error('Error loading farm data:', error);
      }
    });
  }

  initializeSeasons(): void {
    const now = new Date();
    const currentYear = now.getMonth() >= 5 ? now.getFullYear() + 1 : now.getFullYear();
    for (let i = 0; i < 5; i++) {
      const year = currentYear - i;
      this.seasons.push(seasonLabel(year - 1));
    }
    this.seasonOptions = this.seasons.map(season => ({ id: season, name: season }));
    this.selectedSeason = this.seasons[0];
    this.dataService.updateFieldData({ season: this.selectedSeason });
  }

  initializeDateRange(): void {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(yesterday);
    lastWeek.setDate(lastWeek.getDate() - 7);
    this.startDate = lastWeek;
    this.endDate = yesterday;
    this.dataService.updateFieldData({ startDate: lastWeek, endDate: yesterday });
  }

  clearDates(): void {
    this.startDate = null;
    this.endDate = null;
    this.dataService.updateFieldData({ startDate: null, endDate: null });
  }

  onDateChange(): void {
    if (this.startDate && this.endDate) {
      const cropYear = this.endDate.getFullYear();
      const seasonText = seasonLabel(cropYear - 1);
      this.dataService.updateFieldData({ startDate: this.startDate, endDate: this.endDate, userChangedDates: true, season: seasonText, fieldInfoLoading: true });
      const fieldId = this.dataService.getFieldData().fieldId;
      if (fieldId) {
        this.loadFieldContext(fieldId, cropYear);
        this.syncService.getFieldInfo(fieldId, cropYear).subscribe({
          next: (info) => {
            this.dataService.updateFieldData({ crop: info.crop, cultivar: info.cultivar, fieldInfoLoading: false });
          }
        });
      }
    }
  }

  /** The date pickers take and give yyyy-MM-dd; the page keeps local dates. */
  get startIso(): string {
    return this.startDate ? isoDate(this.startDate) : '';
  }

  get endIso(): string {
    return this.endDate ? isoDate(this.endDate) : '';
  }

  onSeasonPicked(season: string): void {
    this.selectedSeason = season;
    this.onSeasonChange();
  }

  /**
   * The UI kit's date picker has no min or max, so a start after the end moves the end up to it, and
   * an end before the start moves the start back: the range never turns inside out.
   */
  onStartPicked(iso: string): void {
    const date = localDay(iso);
    this.startDate = date;
    if (this.endDate && date > this.endDate) this.endDate = date;
    this.onDateChange();
  }

  onEndPicked(iso: string): void {
    const date = localDay(iso);
    this.endDate = date;
    if (this.startDate && date < this.startDate) this.startDate = date;
    this.onDateChange();
  }

  onSeasonChange(): void {
    const startYear = Number(seasonFromLabel(this.selectedSeason)?.from.slice(0, 4));
    const cropYear = startYear + 1;
    this.dataService.updateFieldData({ season: this.selectedSeason, userChangedDates: false, fieldInfoLoading: true });

    const fieldId = this.dataService.getFieldData().fieldId;
    if (fieldId) {
      this.loadFieldContext(fieldId, cropYear);
      this.syncService.getFieldInfo(fieldId, cropYear).subscribe({
        next: (info) => {
          this.dataService.updateFieldData({ crop: info.crop, cultivar: info.cultivar, fieldInfoLoading: false });
        },
        error: (error) => {
          console.error('Error loading field info:', error);
        }
      });
    }
  }
}

/** "2026-10-05" as local midnight on that day, as the old Material picker gave it. */
export function localDay(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}
